import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import {
  MapPin, Globe, Linkedin, Instagram, Twitter, Youtube,
  CalendarDays, Clock, Trophy, ChevronLeft, ChevronRight, ExternalLink,
  Building2, Star, ArrowRight, Play
} from 'lucide-react';
import { resolveAssetUrl } from '../components/contractor/ProfileImageUpload';

const API = process.env.REACT_APP_BACKEND_URL;

function formatDate(d) {
  if (!d) return '';
  try { return new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }); }
  catch { return d; }
}

function daysUntil(dateStr) {
  if (!dateStr) return null;
  const diff = new Date(dateStr) - new Date();
  const days = Math.ceil(diff / 86400000);
  return days;
}

// Simple image carousel for stand photos
function StandCarousel({ images }) {
  const [idx, setIdx] = useState(0);
  const imgs = (images || []).map(resolveAssetUrl).filter(Boolean);
  if (imgs.length === 0) return null;
  return (
    <div className="relative rounded-xl overflow-hidden bg-gray-900" style={{ aspectRatio: '16/9' }}>
      <img src={imgs[idx]} alt="" className="w-full h-full object-cover" />
      {imgs.length > 1 && (
        <>
          <button onClick={() => setIdx(i => (i - 1 + imgs.length) % imgs.length)} className="absolute left-2 top-1/2 -translate-y-1/2 p-1.5 bg-black/50 rounded-full text-white hover:bg-black/80">
            <ChevronLeft className="h-5 w-5" />
          </button>
          <button onClick={() => setIdx(i => (i + 1) % imgs.length)} className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 bg-black/50 rounded-full text-white hover:bg-black/80">
            <ChevronRight className="h-5 w-5" />
          </button>
          <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1">
            {imgs.map((_, i) => (
              <button key={i} onClick={() => setIdx(i)} className={`h-1.5 rounded-full transition-all ${i === idx ? 'w-5 bg-white' : 'w-1.5 bg-white/50'}`} />
            ))}
          </div>
          <span className="absolute top-2 right-2 bg-black/50 text-white text-xs px-2 py-1 rounded-full">{idx + 1}/{imgs.length}</span>
        </>
      )}
    </div>
  );
}

export default function ExhibitorPublicProfile() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    axios.get(`${API}/api/public/exhibitor/${slug}`)
      .then(r => setData(r.data))
      .catch(e => setError(e.response?.status === 404 ? 'Exhibitor not found.' : 'Failed to load profile.'))
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <div className="flex flex-col items-center gap-3">
        <div className="w-10 h-10 rounded-full border-4 border-blue-600 border-t-transparent animate-spin" />
        <span className="text-gray-500 text-sm">Loading profile…</span>
      </div>
    </div>
  );

  if (error) return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <div className="text-center">
        <p className="text-xl font-semibold text-gray-800 mb-2">{error}</p>
        <Link to="/" className="text-blue-600 text-sm hover:underline">← Back to homepage</Link>
      </div>
    </div>
  );

  const { org, profile: p } = data;
  const stats = p.stats || {};
  const coverSrc = resolveAssetUrl(p.cover_image_url);
  const logoSrc = resolveAssetUrl(p.logo_url);
  const upcomingShows = (p.upcoming_shows || []).filter(s => s.status !== 'cancelled').sort((a, b) => new Date(a.start_date) - new Date(b.start_date));
  const pastShows = p.past_shows || [];
  const stands = (p.stands || []).filter(s => s.status !== 'archived');

  return (
    <div className="min-h-screen bg-gray-50">
      {/* ── Hero ── */}
      <div className="relative h-72 md:h-96 bg-gradient-to-br from-blue-900 to-blue-700 overflow-hidden">
        {coverSrc && <img src={coverSrc} alt="cover" className="absolute inset-0 w-full h-full object-cover opacity-40" onError={e => e.target.style.display = 'none'} />}
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 px-6 pb-8 md:px-12">
          <div className="max-w-5xl mx-auto flex items-end gap-6">
            <div className="flex-shrink-0 w-20 h-20 md:w-28 md:h-28 rounded-2xl border-4 border-white shadow-xl bg-white overflow-hidden">
              {logoSrc
                ? <img src={logoSrc} alt="logo" className="w-full h-full object-cover" onError={e => e.target.style.display = 'none'} />
                : <div className="w-full h-full bg-blue-600 flex items-center justify-center text-white text-3xl font-bold">{org.display_name?.[0] || 'E'}</div>
              }
            </div>
            <div className="pb-1">
              <h1 className="text-2xl md:text-4xl font-bold text-white drop-shadow">{org.display_name}</h1>
              {p.tagline && <p className="text-blue-200 mt-1 text-sm md:text-base max-w-2xl">{p.tagline}</p>}
              {(org.city || org.country) && (
                <div className="flex items-center gap-1 text-blue-200 text-xs mt-1.5">
                  <MapPin className="h-3.5 w-3.5" />{[org.city, org.country].filter(Boolean).join(', ')}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* ── Stats Bar ── */}
      {(stats.shows_participated || stats.countries_exhibited || stats.years_exhibiting || stats.total_stand_sqm) ? (
        <div className="bg-blue-700 text-white">
          <div className="max-w-5xl mx-auto px-6 md:px-12 py-4 grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { label: 'Shows Participated', value: stats.shows_participated },
              { label: 'Countries Exhibited', value: stats.countries_exhibited },
              { label: 'Years Exhibiting', value: stats.years_exhibiting },
              { label: 'Total Stand m²', value: stats.total_stand_sqm?.toLocaleString() },
            ].filter(s => s.value).map(s => (
              <div key={s.label} className="text-center">
                <p className="text-2xl font-bold">{s.value}</p>
                <p className="text-blue-200 text-xs mt-0.5">{s.label}</p>
              </div>
            ))}
          </div>
        </div>
      ) : null}

      <div className="max-w-5xl mx-auto px-6 md:px-12 py-10 space-y-14">
        {/* ── About ── */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          <div className="md:col-span-2">
            {p.about && (
              <section>
                <h2 className="text-xl font-bold text-gray-900 mb-3">About Us</h2>
                <p className="text-gray-600 leading-relaxed">{p.about}</p>
              </section>
            )}
          </div>
          {/* Sidebar */}
          <div className="space-y-5">
            {p.industries?.length > 0 && (
              <div>
                <h3 className="text-xs font-bold uppercase tracking-widest text-gray-400 mb-2">Industries</h3>
                <div className="flex flex-wrap gap-1.5">
                  {p.industries.map(ind => <span key={ind} className="bg-blue-50 text-blue-700 text-xs px-2.5 py-1 rounded-full font-medium">{ind}</span>)}
                </div>
              </div>
            )}
            {(p.headquarters_country || p.company_size || p.founded_year) && (
              <div className="space-y-2">
                {p.headquarters_city && <div className="flex items-center gap-2 text-sm text-gray-600"><MapPin className="h-4 w-4 text-gray-400 flex-shrink-0" />{[p.headquarters_city, p.headquarters_country].filter(Boolean).join(', ')}</div>}
                {p.founded_year && <div className="flex items-center gap-2 text-sm text-gray-600"><Building2 className="h-4 w-4 text-gray-400 flex-shrink-0" />Founded {p.founded_year}</div>}
                {p.company_size && <div className="flex items-center gap-2 text-sm text-gray-600"><Star className="h-4 w-4 text-gray-400 flex-shrink-0" />{p.company_size} employees</div>}
              </div>
            )}
            {/* Social links */}
            {p.social && (
              <div>
                <h3 className="text-xs font-bold uppercase tracking-widest text-gray-400 mb-2">Online</h3>
                <div className="flex flex-wrap gap-2">
                  {p.social.website && <a href={p.social.website} target="_blank" rel="noreferrer" className="flex items-center gap-1 text-xs bg-gray-100 hover:bg-blue-50 text-gray-700 hover:text-blue-700 px-2.5 py-1.5 rounded-lg transition-colors"><Globe className="h-3.5 w-3.5" />Website</a>}
                  {p.social.linkedin && <a href={p.social.linkedin} target="_blank" rel="noreferrer" className="flex items-center gap-1 text-xs bg-gray-100 hover:bg-blue-50 text-gray-700 hover:text-blue-700 px-2.5 py-1.5 rounded-lg transition-colors"><Linkedin className="h-3.5 w-3.5" />LinkedIn</a>}
                  {p.social.instagram && <a href={p.social.instagram} target="_blank" rel="noreferrer" className="flex items-center gap-1 text-xs bg-gray-100 hover:bg-blue-50 text-gray-700 hover:text-blue-700 px-2.5 py-1.5 rounded-lg transition-colors"><Instagram className="h-3.5 w-3.5" />Instagram</a>}
                  {p.social.twitter && <a href={p.social.twitter} target="_blank" rel="noreferrer" className="flex items-center gap-1 text-xs bg-gray-100 hover:bg-blue-50 text-gray-700 hover:text-blue-700 px-2.5 py-1.5 rounded-lg transition-colors"><Twitter className="h-3.5 w-3.5" />Twitter</a>}
                  {p.social.youtube && <a href={p.social.youtube} target="_blank" rel="noreferrer" className="flex items-center gap-1 text-xs bg-gray-100 hover:bg-blue-50 text-gray-700 hover:text-blue-700 px-2.5 py-1.5 rounded-lg transition-colors"><Youtube className="h-3.5 w-3.5" />YouTube</a>}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* ── Upcoming Shows (Timeline) ── */}
        {upcomingShows.length > 0 && (
          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-6">Upcoming Exhibitions</h2>
            <div className="relative">
              {/* Timeline line */}
              <div className="absolute left-6 top-0 bottom-0 w-0.5 bg-blue-100" />
              <div className="space-y-6">
                {upcomingShows.map((show) => {
                  const days = daysUntil(show.start_date);
                  const imgSrc = resolveAssetUrl(show.image_url);
                  return (
                    <div key={show.id} className="relative flex gap-6">
                      {/* Timeline dot */}
                      <div className={`flex-shrink-0 w-12 h-12 rounded-full border-4 border-white shadow flex items-center justify-center z-10 ${show.status === 'confirmed' ? 'bg-blue-600' : 'bg-yellow-500'}`}>
                        <CalendarDays className="h-5 w-5 text-white" />
                      </div>
                      <div className="flex-1 bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden hover:shadow-md transition-shadow">
                        <div className="flex flex-col md:flex-row">
                          {imgSrc && (
                            <div className="md:w-48 flex-shrink-0">
                              <img src={imgSrc} alt="" className="w-full h-40 md:h-full object-cover" onError={e => e.target.parentElement.style.display = 'none'} />
                            </div>
                          )}
                          <div className="p-5 flex-1">
                            <div className="flex items-start justify-between gap-3 mb-2">
                              <h3 className="font-bold text-gray-900">{show.show_name}</h3>
                              <div className="flex items-center gap-2 flex-shrink-0">
                                {days !== null && days > 0 && (
                                  <span className="text-xs bg-blue-50 text-blue-700 px-2 py-1 rounded-full font-semibold whitespace-nowrap">{days} days away</span>
                                )}
                                <span className={`text-xs px-2 py-1 rounded-full ${show.status === 'confirmed' ? 'bg-green-50 text-green-700' : 'bg-yellow-50 text-yellow-700'}`}>{show.status}</span>
                              </div>
                            </div>
                            {(show.city || show.venue) && (
                              <div className="flex items-center gap-1.5 text-sm text-gray-500 mb-1">
                                <MapPin className="h-3.5 w-3.5" />
                                {[show.venue, show.city, show.country].filter(Boolean).join(' · ')}
                              </div>
                            )}
                            {(show.start_date || show.end_date) && (
                              <div className="flex items-center gap-1.5 text-sm text-gray-500 mb-3">
                                <CalendarDays className="h-3.5 w-3.5" />
                                {formatDate(show.start_date)}{show.end_date && show.end_date !== show.start_date ? ` – ${formatDate(show.end_date)}` : ''}
                                {(show.hall || show.booth_number) && <span className="ml-2 text-blue-600 font-medium">· {[show.hall, show.booth_number].filter(Boolean).join(' / ')}</span>}
                              </div>
                            )}
                            {show.description && <p className="text-sm text-gray-600 leading-relaxed mb-3">{show.description}</p>}
                            {show.ticket_url && (
                              <a href={show.ticket_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-sm text-blue-600 font-medium hover:text-blue-800">
                                Visit show website <ArrowRight className="h-3.5 w-3.5" />
                              </a>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </section>
        )}

        {/* ── Stands Gallery ── */}
        {stands.length > 0 && (
          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-6">Exhibition Stands</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {stands.map(stand => (
                <div key={stand.id} className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden hover:shadow-md transition-shadow">
                  <StandCarousel images={stand.images} />
                  <div className="p-5">
                    <div className="flex items-start justify-between mb-2">
                      <h3 className="font-bold text-gray-900">{stand.title}</h3>
                      {stand.sqm && <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded-full ml-2 flex-shrink-0">{stand.sqm} m²</span>}
                    </div>
                    {stand.show_name && (
                      <div className="flex items-center gap-1.5 text-sm text-blue-600 font-medium mb-1">
                        <CalendarDays className="h-3.5 w-3.5" />{stand.show_name}{stand.year && ` · ${stand.year}`}
                      </div>
                    )}
                    {(stand.venue || stand.city) && (
                      <div className="flex items-center gap-1.5 text-sm text-gray-500 mb-3">
                        <MapPin className="h-3.5 w-3.5" />{[stand.venue, stand.city, stand.country].filter(Boolean).join(', ')}
                      </div>
                    )}
                    {stand.description && <p className="text-sm text-gray-600 leading-relaxed">{stand.description}</p>}
                    {stand.video_url && (
                      <a href={stand.video_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 mt-3 text-sm text-blue-600 font-medium hover:text-blue-800">
                        <Play className="h-3.5 w-3.5" />Watch video walkthrough
                      </a>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* ── Awards ── */}
        {p.awards?.length > 0 && (
          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-5">Awards & Recognition</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {p.awards.map(award => (
                <div key={award.id} className="bg-white border border-yellow-100 rounded-2xl p-5 flex items-start gap-3 shadow-sm">
                  <div className="flex-shrink-0 w-10 h-10 bg-yellow-50 rounded-full flex items-center justify-center">
                    <Trophy className="h-5 w-5 text-yellow-500" />
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900 text-sm">{award.title}</p>
                    {award.organization && <p className="text-xs text-gray-500 mt-0.5">{award.organization}</p>}
                    {award.year && <p className="text-xs text-gray-400 mt-0.5">{award.year}</p>}
                    {award.url && <a href={award.url} target="_blank" rel="noreferrer" className="text-xs text-blue-600 hover:underline mt-1 inline-flex items-center gap-1">View <ExternalLink className="h-3 w-3" /></a>}
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* ── Past Shows ── */}
        {pastShows.length > 0 && (
          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-4">Exhibition History</h2>
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    {['Show', 'Location', 'Year', 'Size'].map(h => <th key={h} className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wide px-5 py-3">{h}</th>)}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {pastShows.map(s => (
                    <tr key={s.id} className="hover:bg-gray-50">
                      <td className="px-5 py-3 font-medium text-gray-800">{s.show_name}</td>
                      <td className="px-5 py-3 text-gray-500">{[s.city, s.country].filter(Boolean).join(', ') || '—'}</td>
                      <td className="px-5 py-3 text-gray-500">{s.year || '—'}</td>
                      <td className="px-5 py-3 text-gray-500">{s.sqm ? `${s.sqm} m²` : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        )}
      </div>

      {/* Footer */}
      <div className="border-t bg-white mt-10 py-6 text-center text-xs text-gray-400">
        <span>Powered by </span><span className="font-semibold text-blue-600">ExpoStand</span>
      </div>
    </div>
  );
}
